import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createStatusControlThunk,
  deleteStatusControlThunk,
  getStatusControlListThunk,
  updateStatusControlThunk,
} from '../../../../redux/ControlPanel/StatusControl/StatusControl.thunk';

import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import BadgeComponent from '../../../../components/Badge.component';
import ControlCreateEditModal, {
  MainGroupSelectOption,
} from '../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';
import { SortTypes } from '../../../../types/sort.types';

import SearchIcon from '../../../../components/Icons/Search.icon';
import DragIcon from '../../../../components/Icons/Drag.icon';

import { StatusColor } from '../../../../constants/statusColors.constant';
import { Status } from '../../../../redux/ControlPanel/StatusControl/StatusControl.type';
import { useToast } from '../../../../hooks/useToast';
import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledDragWrapper,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSeparator,
  StyledStatusOrder,
} from './sharedStyledComponents';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { useSelector } from 'react-redux';
import {
  getStatusControlList,
  getStatusControlListPagination,
  setStatusControlList,
} from '../../../../redux/ControlPanel/StatusControl/StatusControl.slice';
import { getStatusColor } from '../../../../utils/colors';
import styled from 'styled-components';
import { StyledStatusGroup } from '../../Internship/Internship.styles';

import { truncateText } from '../../../../utils/textTruncate';
import { InternshipStatus } from '../../../../redux/InternshipStatus/InternshipStatus.type';

type StatusWithMainGroup = Status & { mainGroup: MainGroupSelectOption };

const sectionType = ControlSectionTypes.STATUSES;

const StyledContentWrapper = styled.div`
  height: 288px;
  overflow-y: scroll;
  margin-top: 10px;
`;

const TruncatedBadgeWrapper = styled.div<{ $isTruncated?: boolean }>`
  display: inline-block;
  ${({ $isTruncated }) => $isTruncated && `
    cursor: help;
  `}
`;

export const StatusesSectionComponent: FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuthHook();
  const { openModal } = useModalManager();
  const { successToast } = useToast();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [orderSort, setOrderSort] = useState(SortTypes.ASC);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const statuses = useSelector(getStatusControlList);
  const pagination = useSelector(getStatusControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_STATUS,
  ]);

  const creatable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_STATUS,
  ]);

  const deletable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_STATUS,
  ]);

  const viewable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_STATUS,
  ]);

  const dragItem = useRef<any>(null);
  const dragOverItem = useRef<any>(null);

  const fetchParams = useMemo(() => {
    return { q: searchQuery };
  }, [searchQuery]);

  const STATUS_MAX_LENGTH = 50;

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getStatusControlListThunk(fetchParams));
  };

  const createNewSectionItem = (newItem: StatusWithMainGroup) => {
    const { name, color, mainGroup, textColor } = newItem;

    if (name && mainGroup) {
      createItemWithModalWrapper(
        createStatusControlThunk,
        getStatusControlListThunk,
        {
          name,
          color,
          textColor,
          code: `${mainGroup.value}_${name.toUpperCase()}`,
          event_id: mainGroup.eventId,
        },
        fetchParams,
        t('Such a status already exists in this group!'),
      );
    }
  };

  const editSectionItem = (sectionItem: StatusWithMainGroup) => {
    const { name, color, mainGroup } = sectionItem;

    if (name && color && mainGroup) {
      createItemWithModalWrapper(
        updateStatusControlThunk,
        getStatusControlListThunk,
        {
          ...sectionItem,
          code: `${mainGroup.value}_${name.toUpperCase()}`,
          event_id: mainGroup.eventId,
        },
        fetchParams,
        t('Such a status already exists in this group!'),
      );
    }
  };

  const deleteSectionItem = (sectionItem: StatusWithMainGroup) => {
    dispatch(deleteStatusControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const updateStatusesOrder = async (statuses: Status[]) => {
    await Promise.all(
      statuses.map((changedStatus: Status) => {
        const params = {
          id: changedStatus.id,
          name: changedStatus.name,
          color: changedStatus.color || StatusColor.GREY,
          order: changedStatus.order,
          event_id: changedStatus.event.id,
        };

        return dispatch(updateStatusControlThunk(params));
      }),
    );
  };

  const updateOrder = async () => {
    const changedStatuses = statuses.filter(
      (d: any) => d.oldOrder !== undefined && d.order !== d.oldOrder,
    );

    await updateStatusesOrder(changedStatuses as Status[]);

    successToast(t('orderUpdated'));

    updateListAfterAction();
  };

  const handleDragStart = (e: React.DragEvent<HTMLLIElement>, status: any) => {
    dragItem.current = status;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLIElement>, status: any) => {
    dragOverItem.current = status;
    setDragOverIndex(status.id);
  };

  const handleDrop = (e: React.DragEvent<HTMLLIElement>) => {
    if (dragItem.current?.event?.id === dragOverItem.current?.event?.id) {
      const data = [...statuses];
      const dragItemIndex = data.findIndex(d => d.id === dragItem?.current?.id);
      const dragOverItemIndex = data.findIndex(
        d => d.id === dragOverItem?.current?.id,
      );

      data.splice(dragItemIndex, 1);
      data.splice(dragOverItemIndex, 0, dragItem?.current);

      data
        .filter((d: any) => d.event.id === dragItem.current?.event?.id)
        .filter((d: any) => d.event.id === dragItem.current?.event?.id)
        .map((d: any, index: number) => ({
          ...d,
          order: index + 1,
          oldOrder: d.order,
        }))
        .forEach((changedItem: any) => {
          const changedItemIndex = data?.findIndex(
            d => d.id === changedItem.id,
          );

          if (changedItemIndex != null) {
            data?.splice(changedItemIndex, 1, changedItem);
          }
        });
      dragItem.current = null;
      dragOverItem.current = null;
      updateItems(data);
    }

    setDragOverIndex(null);
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getStatusControlListThunk({
        q: value,
        page: 1,
        sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const updateItems = (items: Status[]) => {
    dispatch(setStatusControlList(items));
  };

  useEffect(() => {
    dispatch(getStatusControlListThunk(fetchParams));
  }, []);

  if (!viewable) return null;

  const groupedItems = Object.entries(
    Object.groupBy(statuses, ({ event }) => event.name),
  );

  const getBadgeProps = (status: Status) => {
    const displayName = truncateText(status.name, STATUS_MAX_LENGTH);
    const isTruncated = status.name.length > STATUS_MAX_LENGTH;

    return {
      text: displayName,  // переименовали displayName → text
      isTruncated,
      color: getStatusColor(status.color),  // badgeColor → color
      textColor: getStatusColor(status.textColor),  // badgeTextColor → textColor
      title: isTruncated ? status.name : undefined
    };
  };

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('Status management')}</StyledHeaderLabel>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {editable && (
              <ButtonComponent
                handleClick={updateOrder}
                textColor="#588fff"
                borderColor="#588fff"
              >
                {t('Save order')}
              </ButtonComponent>
            )}

            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>

        <StyledContentWrapper id={`scrollableDiv-${sectionType}`}>
          {groupedItems.map(([title, categories]) => {
            return (
              <>
                <StyledStatusGroup>
                  <span>{title}</span>
                </StyledStatusGroup>
                {(categories as Status[]).map((category: Status) => {
                  const badgeProps = getBadgeProps(category);
                  return (
                  <>
                    {dragOverIndex === category.id && <StyledSeparator />}

                    <StyledControlListItem
                      onClick={() =>
                        openModal(ControlCreateEditModal, {
                          sectionType,
                          sectionItem: category,
                          editSectionItem,
                          deleteSectionItem,
                          editable,
                          deletable,
                        })
                      }
                      onDragStart={e => handleDragStart(e, category)}
                      onDragEnter={e => handleDragEnter(e, category)}
                      onDragEnd={handleDrop}
                      key={category.id}
                      draggable={!searchQuery}
                    >
                      <StyledDragWrapper>
                        {!searchQuery && (
                          <>
                            <DragIcon />
                            <StyledStatusOrder>
                              {category.order}
                            </StyledStatusOrder>
                          </>
                        )}
                        <TruncatedBadgeWrapper
                          $isTruncated={badgeProps.isTruncated}
                          title={badgeProps.title}
                        >
                          <BadgeComponent
                            appearance="default"
                            {...badgeProps}
                          />
                        </TruncatedBadgeWrapper>
                      </StyledDragWrapper>
                    </StyledControlListItem>
                  </>
                )})}
              </>
            );
          })}
        </StyledContentWrapper>
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
